import { makeAutoObservable } from "mobx"

/** Trễ trước khi vẽ thanh — tác vụ xong nhanh hơn mốc này thì không vẽ gì cả. */
const SHOW_DELAY_MS = 150

/** Đã vẽ thì giữ tối thiểu bấy nhiêu, để lần hiện nào cũng đủ lâu để mắt kịp nhận ra. */
const MIN_VISIBLE_MS = 400

/** Nhịp bò của tiến độ giả lập. */
const TRICKLE_INTERVAL_MS = 200

/**
 * Trần của tiến độ GIẢ LẬP. Không bao giờ tự bò quá mốc này: 100% chỉ được đặt khi tác vụ thật đã xong,
 * nếu không thanh sẽ đầy rồi đứng im — trông như đã xong mà màn hình vẫn chưa đổi.
 */
const CAP_PERCENT = 90

/** Nhảy thẳng tới đây ngay khi hiện, để lần vẽ đầu đã thấy có thanh thay vì một vạch 0. */
const INITIAL_PERCENT = 8

/**
 * Mỗi nhịp đi được bấy nhiêu phần của quãng CÒN LẠI tới trần.
 * Vì quãng còn lại co dần nên bước đi cũng nhỏ dần — nhanh lúc đầu, chậm dần về sau, tiệm cận 90%
 * và không bao giờ chạm. Đây là lý do request 30s vẫn có cảm giác "đang chạy".
 */
const APPROACH_RATE = 0.1

/** Còn cách trần dưới ngưỡng này thì ngừng bò — tránh giữ interval chạy mãi để cộng những lượng vô nghĩa. */
const APPROACH_EPSILON = 0.5

/** Chờ hết fade-out (200ms trong `index.less`) rồi mới trả về 0, để không thấy thanh giật ngược. */
const RESET_DELAY_MS = 200

/**
 * Trạng thái của thanh progress loading dùng chung (`UiProgressBar` dưới header).
 *
 * Store CHỈ giữ logic đếm tác vụ và canh thời gian — không biết gì về DOM/CSS, nên test được bằng
 * fake timers mà không cần render.
 *
 * Hai điểm là nguyên nhân gốc của cảm giác "giật" mà thiết kế này chặn:
 *  - **Trễ 150ms trước khi hiện**: request 80ms sẽ không vẽ gì, thay vì lóe lên một cú rồi tắt.
 *  - **Giữ tối thiểu 400ms**: đã hiện thì không tắt ngay lập tức, tránh nhấp nháy.
 *
 * Dùng refcount thay cho boolean: hai tác vụ song song chỉ có MỘT thanh, và thanh chỉ tắt khi tác vụ
 * cuối cùng kết thúc. Với boolean, tác vụ nhanh xong trước sẽ tắt thanh của tác vụ còn đang chạy.
 *
 * ## Tiến độ giả lập
 *
 * `percent` KHÔNG phải tiến độ thật — hầu hết API không báo được điều đó. Nó tự bò tiệm cận
 * {@link CAP_PERCENT} với bước nhỏ dần, và chỉ nhảy lên 100 khi tác vụ thật kết thúc. Trần 90% là có
 * chủ đích: thanh đầy trong lúc còn đang chờ sẽ khiến người dùng tưởng đã xong.
 */
class UiProgressStore {
    /** Có đang vẽ thanh hay không. */
    isVisible = false

    /**
     * Tiến độ hiển thị, 0–100. GIẢ LẬP: không phản ánh phần trăm thật của request (HTTP không cho biết
     * điều đó cho phần lớn API), chỉ để người dùng thấy hệ thống đang chạy. Tự bò tới tối đa
     * {@link CAP_PERCENT}; chỉ tác vụ thật kết thúc mới đẩy lên 100.
     */
    percent = 0

    private pending = 0
    private shownAt = 0
    private showTimer: ReturnType<typeof setTimeout> | null = null
    private hideTimer: ReturnType<typeof setTimeout> | null = null
    private trickleTimer: ReturnType<typeof setInterval> | null = null
    private resetTimer: ReturnType<typeof setTimeout> | null = null

    constructor() {
        makeAutoObservable(this)
    }

    /** Báo có một tác vụ bắt đầu. Luôn phải có một `done()` tương ứng. */
    start() {
        this.pending += 1

        if (this.pending > 1) {
            return
        }

        // Tác vụ mới trong lúc đang chờ tắt → giữ thanh chạy tiếp, không để nó tắt rồi bật lại.
        this.clearHideTimer()

        if (this.isVisible) {
            // Thanh còn hiện nhưng chu kỳ trước đã chốt 100 ⇒ đây là chu kỳ MỚI: kéo về mốc đầu và bò
            // lại. Không làm thì thanh nằm im ở 100 suốt tác vụ mới, trông như đã xong mà vẫn quay.
            this.clearResetTimer()
            this.percent = INITIAL_PERCENT
            this.startTrickle()
            return
        }

        if (this.showTimer) {
            return
        }

        this.showTimer = setTimeout(() => {
            this.showTimer = null
            this.isVisible = true
            this.shownAt = Date.now()
            this.clearResetTimer()
            this.percent = INITIAL_PERCENT
            this.startTrickle()
        }, SHOW_DELAY_MS)
    }

    /** Báo một tác vụ kết thúc (thành công hay lỗi đều gọi). */
    done() {
        // Không cho xuống âm: một chỗ gọi lệch sẽ làm mọi lần start() sau đó không bao giờ hiện được thanh.
        this.pending = Math.max(0, this.pending - 1)

        if (this.pending > 0) {
            return
        }

        if (!this.isVisible) {
            this.clearShowTimer()
            this.stopTrickle()
            this.percent = 0
            return
        }

        // Tác vụ thật đã xong ⇒ chốt 100 ngay, kể cả khi còn phải chờ hết min-visible: người dùng thấy
        // thanh chạy nốt tới đầu rồi mới mờ đi, thay vì mờ đi giữa chừng.
        this.stopTrickle()
        this.percent = 100

        const remain = MIN_VISIBLE_MS - (Date.now() - this.shownAt)
        if (remain <= 0) {
            this.hide()
            return
        }

        this.clearHideTimer()
        this.hideTimer = setTimeout(() => {
            this.hideTimer = null
            this.hide()
        }, remain)
    }

    /**
     * Bọc một promise: thanh chạy trong lúc chờ, tắt khi xong.
     * KHÔNG bắt lỗi — lỗi vẫn nổi lên caller để luồng xử lý lỗi hiện tại giữ nguyên.
     */
    async track<T>(promise: Promise<T>): Promise<T> {
        this.start()
        try {
            return await promise
        } finally {
            this.done()
        }
    }

    /** Tắt thanh và hẹn trả tiến độ về 0 SAU khi fade-out xong, để lần hiện sau không bắt đầu từ 100. */
    private hide() {
        this.isVisible = false

        this.clearResetTimer()
        this.resetTimer = setTimeout(() => {
            this.resetTimer = null
            this.percent = 0
        }, RESET_DELAY_MS)
    }

    /**
     * Bò dần về phía trần: mỗi nhịp đi một phần của quãng CÒN LẠI nên bước đi tự nhỏ dần.
     * Ngừng hẳn khi đã sát trần — giữ interval chạy tiếp chỉ để cộng những lượng không nhìn thấy được.
     */
    private startTrickle() {
        this.stopTrickle()

        this.trickleTimer = setInterval(() => {
            const gap = CAP_PERCENT - this.percent

            if (gap <= APPROACH_EPSILON) {
                this.stopTrickle()
                return
            }

            this.percent += gap * APPROACH_RATE
        }, TRICKLE_INTERVAL_MS)
    }

    private stopTrickle() {
        if (this.trickleTimer) {
            clearInterval(this.trickleTimer)
            this.trickleTimer = null
        }
    }

    private clearResetTimer() {
        if (this.resetTimer) {
            clearTimeout(this.resetTimer)
            this.resetTimer = null
        }
    }

    private clearShowTimer() {
        if (this.showTimer) {
            clearTimeout(this.showTimer)
            this.showTimer = null
        }
    }

    private clearHideTimer() {
        if (this.hideTimer) {
            clearTimeout(this.hideTimer)
            this.hideTimer = null
        }
    }
}

export default UiProgressStore

const uiProgressStoreInstance = new UiProgressStore()
export { uiProgressStoreInstance }
